import React, { useEffect, useState } from 'react';
import { CalendarClock, Loader2, PhoneCall, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../api';
import { usePortal } from '../PortalAuth';
import { PageHeader, Card, SectionTitle, inputCls, goldBtn, ghostBtn } from '../ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RequestPrice } from './RequestServicePage';

const calendlySrc = (url, user) => {
  const q = new URLSearchParams({ embed_domain: window.location.hostname, embed_type: 'Inline', hide_gdpr_banner: '1', name: user?.name || '', email: user?.email || '' });
  return `${url}${url.includes('?') ? '&' : '?'}${q}`;
};

function Booking({ check, onClose, onBooked }) {
  const { user } = usePortal();
  useEffect(() => {
    const onMsg = (e) => { if (e.origin === 'https://calendly.com' && e.data?.event === 'calendly.event_scheduled') onBooked(check); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [check]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0B0D10] border-[#1F242C] text-[#E5E5E5] p-0 overflow-hidden" data-testid="sc-booking-dialog">
        <DialogHeader className="px-6 pt-6"><DialogTitle className="font-heading">Prenota · {check.name}</DialogTitle></DialogHeader>
        <iframe title="Calendly" src={calendlySrc(check.calendly_url, user)} className="w-full h-[70vh] bg-white" data-testid="sc-calendly-frame" />
      </DialogContent>
    </Dialog>
  );
}

function ContactForm({ check, onSent }) {
  const [f, setF] = useState({ description: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post('/requests/requests', { service: 'security_check', check_id: check.id, ...f }); toast.success('Richiesta inviata: ti ricontatteremo a breve'); onSent(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-3" data-testid={`sc-contact-form-${check.id}`}>
      <RequestPrice service="security_check" />
      <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Descrivi l'esigenza (luogo, dispositivi, tempi)" className={`${inputCls} w-full p-3 !h-auto min-h-[90px]`} data-testid={`sc-contact-description-${check.id}`} />
      <div className="flex flex-wrap gap-3">
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Telefono per il contatto" className={`${inputCls} h-10 px-3 flex-1 min-w-[180px]`} data-testid={`sc-contact-phone-${check.id}`} />
        <button disabled={busy || f.description.trim().length < 10} onClick={send} className={`${goldBtn} h-10 px-5 text-sm inline-flex items-center gap-2 disabled:opacity-40`} data-testid={`sc-contact-send-${check.id}`}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}Invia</button>
      </div>
    </div>
  );
}

function CheckCard({ check, open, onToggle, onBook, onSent }) {
  return (
    <Card className="p-6 flex flex-col" data-testid={`sc-card-${check.id}`}>
      <h3 className="font-heading text-lg text-[#F2F2F2]" data-testid={`sc-name-${check.id}`}>{check.name}</h3>
      {check.description && <p className="text-sm text-[#A3A3A3] mt-2 flex-1">{check.description}</p>}
      {check.price_label && <p className="text-sm font-mono text-[#D4AF37] mt-3" data-testid={`sc-price-${check.id}`}>{check.price_label}</p>}
      <div className="flex flex-wrap gap-2 mt-5">
        {check.calendly_url && <button onClick={() => onBook(check)} className={`${goldBtn} h-10 px-4 text-sm inline-flex items-center gap-2`} data-testid={`sc-book-${check.id}`}><CalendarClock size={14} />Prenota un appuntamento</button>}
        <button onClick={onToggle} className={`${ghostBtn} h-10 px-4 text-sm inline-flex items-center gap-2`} data-testid={`sc-contact-${check.id}`}><PhoneCall size={14} />Chiedi di essere contattato</button>
      </div>
      {open && <ContactForm check={check} onSent={onSent} />}
    </Card>
  );
}

export default function SecurityChecksPage() {
  const [checks, setChecks] = useState(null);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [booking, setBooking] = useState(null);
  const loadRows = () => api.get('/requests/requests').then((r) => setRows(r.data.filter((x) => x.service === 'security_check'))).catch(() => {});
  useEffect(() => { api.get('/security-checks').then((r) => setChecks(r.data)).catch((e) => { toast.error(errMsg(e)); setChecks([]); }); loadRows(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const booked = async (check) => {
    setBooking(null);
    try { await api.post('/requests/requests', { service: 'security_check', check_id: check.id, booked: true, description: 'Appuntamento prenotato tramite Calendly' }); toast.success('Appuntamento prenotato'); loadRows(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!checks) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  return (
    <div data-testid="security-checks-page">
      <PageHeader kicker="Cyber Security & Data Leakage" title="Verifiche di sicurezza e Data Leakage" />
      <p className="text-sm text-[#A3A3A3] mb-6 max-w-2xl">Scegli la verifica: puoi prenotare direttamente un appuntamento oppure chiedere di essere ricontattato da un consulente Smart Info.</p>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {checks.map((c) => <CheckCard key={c.id} check={c} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} onBook={setBooking} onSent={() => { setOpen(null); loadRows(); }} />)}
        {checks.length === 0 && <p className="text-sm text-[#7A7F87]" data-testid="sc-empty">Nessuna verifica disponibile al momento.</p>}
      </div>
      <Card><div className="px-6 pt-6"><SectionTitle>Le tue richieste e prenotazioni</SectionTitle></div>
        {rows.length === 0 ? <p className="p-6 text-sm text-[#7A7F87]">Nessuna richiesta.</p> : rows.map((r) => (
          <div key={r.id} className="px-6 py-3 border-t border-[#1F242C] flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid={`sc-row-${r.id}`}>
            <span className="text-[#7A7F87] w-24">{new Date(r.created_at).toLocaleDateString('it-IT')}</span><span className="flex-1 min-w-0 truncate">{r.check_name} · {r.booked ? 'prenotazione Calendly' : r.description}</span><span className="text-[#D4AF37]">{r.status_label}</span></div>
        ))}
      </Card>
      {booking && <Booking check={booking} onClose={() => setBooking(null)} onBooked={booked} />}
    </div>
  );
}
